// Support Tickets page
import { api } from '../api';
import { state, isAdmin, navigate } from '../app';
import { esc, $, $$, setHTML, formatDate, statusBadge, timeAgo, icons } from '../helpers';
import { toast, openModal, closeModal, setBreadcrumb, skeletonTable } from '../ui';

// ─── Ticket List ───────────────────────────────────────────────────────────
export async function renderTickets() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Support Tickets' }]);
  setHTML(content, skeletonTable(6, 5));
  try {
    const data = await api.get('/tickets');
    const tickets = data.tickets || [];
    let rows = '';
    tickets.forEach((t: any) => {
      const priorityClass = t.priority === 'urgent' ? 'color:var(--danger)' : t.priority === 'high' ? 'color:var(--warning)' : '';
      rows += '<tr style="cursor:pointer" data-ticket-id="' + t.id + '">'
        + '<td class="font-mono">' + esc(t.ticket_number) + '</td>'
        + '<td>' + esc(t.subject) + '</td>'
        + (isAdmin() ? '<td>' + esc(t.client_name || '\u2014') + '</td>' : '')
        + '<td>' + statusBadge(t.status) + '</td>'
        + '<td><span style="' + priorityClass + '">' + esc(t.priority) + '</span></td>'
        + '<td>' + statusBadge(t.category) + '</td>'
        + '<td>' + timeAgo(t.updated_at) + '</td>'
        + '<td>' + esc(t.reply_count || 0) + '</td>'
        + '</tr>';
    });
    const tableHtml = !tickets.length
      ? '<div class="empty-state"><p>No tickets yet</p></div>'
      : '<div class="table-wrapper"><table><thead><tr>'
        + '<th>Ticket</th><th>Subject</th>'
        + (isAdmin() ? '<th>Client</th>' : '')
        + '<th>Status</th><th>Priority</th><th>Category</th><th>Updated</th><th>Replies</th>'
        + '</tr></thead><tbody>' + rows + '</tbody></table></div>';
    setHTML(content, '<div class="page-enter"><div class="page-header"><h1>Support Tickets</h1>'
      + '<div class="page-header-actions"><button class="btn btn-primary" id="new-ticket-btn"><i data-lucide="plus"></i> New Ticket</button></div>'
      + '</div><div class="card"><div class="card-body no-pad">' + tableHtml + '</div></div></div>');
    icons();
    // Row click → detail
    $$('[data-ticket-id]').forEach(row => {
      row.addEventListener('click', () => {
        navigate('/tickets/' + row.getAttribute('data-ticket-id'));
      });
    });
    // New ticket button
    document.getElementById('new-ticket-btn')?.addEventListener('click', openNewTicketModal);
  } catch (err: any) { toast(err.message, 'error'); }
}

function openNewTicketModal() {
  openModal('New Support Ticket', '<form id="ticket-form">'
    + '<div class="form-group"><label>Subject *</label><input type="text" name="subject" required maxlength="200" placeholder="Brief description of your issue"></div>'
    + '<div class="form-row"><div class="form-group"><label>Category</label><select name="category"><option value="general">General</option><option value="billing">Billing</option><option value="technical">Technical</option><option value="other">Other</option></select></div>'
    + '<div class="form-group"><label>Priority</label><select name="priority"><option value="normal">Normal</option><option value="low">Low</option><option value="high">High</option><option value="urgent">Urgent</option></select></div></div>'
    + '<div class="form-group"><label>Message *</label><textarea name="content" rows="5" required maxlength="5000" placeholder="Describe your issue in detail..."></textarea></div>'
    + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="ticket-cancel">Cancel</button><button type="submit" class="btn btn-primary">Create Ticket</button></div></form>');
  document.getElementById('ticket-cancel')?.addEventListener('click', closeModal);
  document.getElementById('ticket-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body = Object.fromEntries(fd);
    try {
      const data = await api.post('/tickets', body);
      toast('Ticket ' + data.ticket_number + ' created', 'success');
      closeModal();
      navigate('/tickets/' + data.id);
    } catch (err: any) { toast(err.message, 'error'); }
  });
}

// ─── Ticket Detail ─────────────────────────────────────────────────────────
export async function renderTicketDetail(id: number) {
  const content = $('#content')!;
  setBreadcrumb([
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Support Tickets', href: '/tickets' },
    { label: 'Ticket' },
  ]);
  setHTML(content, '<div class="content-loading"><div class="spinner"></div></div>');
  try {
    const data = await api.get('/tickets/' + id);
    const t = data.ticket;
    const replies = data.replies || [];

    // Header with ticket info
    let statusOptions = '';
    if (isAdmin()) {
      ['open', 'in_progress', 'resolved', 'closed'].forEach(s => {
        statusOptions += '<option value="' + s + '"' + (t.status === s ? ' selected' : '') + '>' + esc(s.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())) + '</option>';
      });
    }

    let headerHtml = '<div class="page-enter"><div class="page-header"><div>'
      + '<h1><span class="font-mono" style="color:var(--accent)">' + esc(t.ticket_number) + '</span> ' + esc(t.subject) + '</h1>'
      + '<div style="display:flex;gap:0.5rem;margin-top:0.4rem;align-items:center">'
      + statusBadge(t.status) + ' ' + statusBadge(t.priority) + ' ' + statusBadge(t.category)
      + (isAdmin() ? ' <span class="text-muted">\u2014 ' + esc(t.client_name) + ' (' + esc(t.client_email) + ')</span>' : '')
      + '</div></div>'
      + (isAdmin() ? '<div class="page-header-actions"><select id="status-select" class="btn btn-secondary" style="padding:0.5rem 1rem">' + statusOptions + '</select></div>' : '')
      + '</div>';

    // Replies thread
    let repliesHtml = '<div class="ticket-thread" style="display:flex;flex-direction:column;gap:1rem;margin-top:1rem">';
    replies.forEach((r: any) => {
      const mine = r.sender_id === state.user!.id;
      const isStaff = r.sender_role === 'admin';
      repliesHtml += '<div class="card" style="border-left:3px solid ' + (isStaff ? 'var(--accent)' : 'var(--border)') + '">'
        + '<div class="card-body" style="padding:0.8rem 1rem">'
        + '<div style="display:flex;justify-content:space-between;margin-bottom:0.4rem">'
        + '<strong>' + esc(r.sender_name) + (isStaff ? ' <span style="background:var(--accent);color:white;padding:0.1rem 0.4rem;border-radius:4px;font-size:0.7rem">STAFF</span>' : '') + '</strong>'
        + '<span class="text-muted" style="font-size:0.8rem">' + timeAgo(r.created_at) + '</span>'
        + '</div>'
        + '<div style="white-space:pre-wrap;line-height:1.5">' + esc(r.content) + '</div>';
      if (r.attachment_name) {
        repliesHtml += '<div style="margin-top:0.5rem;padding:0.4rem 0.6rem;background:var(--bg-secondary);border-radius:6px;display:inline-flex;align-items:center;gap:0.4rem">'
          + '<i data-lucide="paperclip" style="width:14px;height:14px"></i> '
          + '<span style="font-size:0.85rem">' + esc(r.attachment_name) + '</span>'
          + (r.attachment_key && r.attachment_key.length > 100
            ? ' <a href="data:application/pdf;base64,' + r.attachment_key + '" download="' + esc(r.attachment_name) + '" class="btn btn-secondary btn-sm" style="padding:0.2rem 0.5rem;font-size:0.75rem">Download</a>'
            : '')
          + '</div>';
      }
      repliesHtml += '</div></div>';
    });
    repliesHtml += '</div>';

    // Reply form (only if not closed)
    let replyForm = '';
    if (t.status !== 'closed') {
      replyForm = '<div class="card mt-2"><div class="card-header"><h3>Reply</h3></div><div class="card-body">'
        + '<form id="reply-form">'
        + '<div class="form-group"><textarea name="content" rows="3" required maxlength="5000" placeholder="Type your reply..."></textarea></div>'
        + '<div style="display:flex;gap:0.5rem;align-items:center">'
        + '<label class="btn btn-secondary" style="cursor:pointer"><i data-lucide="paperclip"></i> Attach PDF <input type="file" id="pdf-upload" accept=".pdf" style="display:none"></label>'
        + '<span id="file-name" class="text-muted" style="font-size:0.85rem"></span>'
        + '<button type="submit" class="btn btn-primary" style="margin-left:auto"><i data-lucide="send"></i> Send</button>'
        + '</div></form></div></div>';
    } else {
      replyForm = '<div class="card mt-2"><div class="card-body"><p class="text-muted" style="text-align:center">This ticket is closed.</p></div></div>';
    }

    setHTML(content, headerHtml + repliesHtml + replyForm + '</div>');
    icons();

    // PDF upload handler
    let pendingFile: { name: string; data: string } | null = null;
    document.getElementById('pdf-upload')?.addEventListener('change', (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast('Only PDF files are allowed', 'error');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast('File too large (max 10MB)', 'error');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        pendingFile = { name: file.name, data: base64 };
        const fileNameEl = document.getElementById('file-name');
        if (fileNameEl) fileNameEl.textContent = file.name;
      };
      reader.readAsDataURL(file);
    });

    // Reply submit
    document.getElementById('reply-form')?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData(e.target as HTMLFormElement);
      const replyContent = (fd.get('content') as string)?.trim();
      if (!replyContent && !pendingFile) return;
      try {
        // If there's a file, upload it first
        if (pendingFile) {
          await api.post('/tickets/' + id + '/attachment', {
            file_name: pendingFile.name,
            file_data: pendingFile.data,
          });
          pendingFile = null;
        }
        if (replyContent) {
          await api.post('/tickets/' + id + '/reply', { content: replyContent });
        }
        toast('Reply sent', 'success');
        renderTicketDetail(id); // Reload
      } catch (err: any) { toast(err.message, 'error'); }
    });

    // Admin status change
    document.getElementById('status-select')?.addEventListener('change', async (e) => {
      const newStatus = (e.target as HTMLSelectElement).value;
      try {
        await api.put('/tickets/' + id + '/status', { status: newStatus });
        toast('Status updated to ' + newStatus.replace(/_/g, ' '), 'success');
        renderTicketDetail(id);
      } catch (err: any) { toast(err.message, 'error'); }
    });

  } catch (err: any) { toast(err.message, 'error'); }
}
